import clsx from 'clsx';
import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';

import styles from './ButtonGroup.module.scss';

interface CustomCssProperties extends CSSProperties {
  '--custom-active-bg': string;
}

export interface ButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  labels: ReactNode[];
  ariaLabels?: string[];
  activeIndex?: number;
  onClick?: (index: number) => void;
  customActiveBackgroundColor?: string;
  disabledIndices?: number[];
}

export const ButtonGroup = ({
  labels,
  ariaLabels,
  activeIndex = 0,
  onClick,
  customActiveBackgroundColor,
  className,
  disabledIndices = [],
  ...rest
}: ButtonGroupProps) => {
  const [index, setIndex] = useState(activeIndex);

  useEffect(() => {
    setIndex(activeIndex);
  }, [activeIndex]);

  const handleClick = (clickIndex: number) => () => {
    setIndex(clickIndex);
    onClick?.(clickIndex);
  };

  const activeStyle: CustomCssProperties | undefined =
    customActiveBackgroundColor
      ? { '--custom-active-bg': customActiveBackgroundColor }
      : undefined;

  return (
    <div
      className={clsx(styles.root, className)}
      role="group"
      data-button-group
      {...rest}
    >
      {labels.map((label, _index) => {
        const isActive = index === _index;
        const isDisabled = disabledIndices.includes(_index);

        return (
          <button
            key={_index}
            type="button"
            data-testid={`button-group-item-${_index}`}
            className={clsx(styles.item, {
              [styles.itemActive]: isActive,
              [styles.itemDisabled]: isDisabled,
            })}
            data-button-group-item
            onClick={handleClick(_index)}
            data-is-active={isActive}
            aria-label={ariaLabels?.[_index]}
            style={isActive ? activeStyle : undefined}
            disabled={isDisabled}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};
